'use client';

/**
 * Bootstrap + read/write hook for `/explorer/listing_alert/` — now the
 * unified renter-profile store on the FE. Same endpoint powers three
 * surfaces:
 *   - the classic listing-alerts card on /saved (existing)
 *   - the renter-profile wizard modal (post-review + home banner)
 *   - the profile-page preferences hub
 *
 * All three read the same shape (basics + preferred_* + completion
 * flag). We didn't fork into a second hook because the semantics
 * ARE identical — one endpoint, one Explorer row.
 *
 * The `useRenterProfile` alias at the bottom of this file exists
 * purely so the renter-profile UI code reads self-documenting.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getListingAlert,
  updateListingAlert,
  type ListingAlertResponse,
  type UpdateListingAlertRequest,
  type PreferredPets,
  type PreferredCommuteTime,
} from '@repo/api';
import { usePathname } from 'next/navigation';
import {
  loadListingAlert,
  getListingAlertCache,
  setListingAlertCache,
  clearListingAlertCache,
  subscribeToListingAlert,
} from './listingAlertStore';
import { useStore } from '@/providers/StoreProvider';
import { useAnalytics } from '@/hooks/useAnalytics';
import { ensureSession } from '@/lib/api';
import { hasAllListingAlertBasics, resolveListingAlertAutoSource } from '@/utils/listingAlerts';

const isMissingArrayField = (value: string[] | number[] | null | undefined) =>
  !Array.isArray(value) || value.length === 0;

// Default payload used when the initial fetch fails OR the store
// hasn't hydrated yet. Kept at the top so every setter's rollback
// branch has one source of truth.
const EMPTY_PROFILE: ListingAlertResponse = {
  listingAlert: false,
  listingAlertNeighborhoodIds: null,
  listingAlertMaxRent: null,
  listingAlertNumBedrooms: null,
  listingAlertAutoEnabledAt: null,
  preferredMinMoveInDate: null,
  preferredMaxMoveInDate: null,
  preferredPets: null,
  preferredPetsDetail: '',
  preferredAmenities: null,
  preferredWorkLocation: '',
  preferredSchoolLocation: '',
  preferredCommuteTime: null,
  isRenterProfileCompleted: false,
};

/**
 * Fetches and updates `/explorer/listing_alert/`; defaults to off when the request fails.
 */
export function useListingAlert() {
  const [data, setData] = useState<ListingAlertResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const didAttemptBootstrapRef = useRef(false);
  const analytics = useAnalytics();
  const pathname = usePathname();
  // Mirror of `data` for synchronous reads inside the update callback.
  // `updateListingAlertFields` has to know the committed pre-patch
  // state to detect a genuine off → on transition, and reading `data`
  // there would either capture a stale closure or force the callback
  // to re-create on every state change.
  const dataRef = useRef<ListingAlertResponse | null>(null);
  dataRef.current = data;
  // The endpoint is auth-protected — skip the fetch when the user
  // isn't logged in, otherwise we get a stream of 403s (e.g. on the
  // listings page which mounts several components that use this hook).
  const isLoggedIn = useStore(s => s.webAuth.isLoggedIn);
  // Renter-profile bootstrap sources only. The three listing-alert
  // basics are NOT read here — `ListingAlertAutoEnable` owns syncing
  // those from the filter store (see the note in `load` below).
  //
  // Move-in comes off the listings filters slice as a date range (same
  // shape the renter profile stores it); amenities are the camelCase
  // toggle map we reverse-map to snake_case codes below.
  const persistedMinMoveInDate = useStore(s => s.listingsFilters.expectedMoveInDate.min);
  const persistedMaxMoveInDate = useStore(s => s.listingsFilters.expectedMoveInDate.max);
  const persistedAmenities = useStore(s => s.listingsFilters.amenities);
  const persistedSearchFiltersRef = useRef({
    minMoveInDate: persistedMinMoveInDate,
    maxMoveInDate: persistedMaxMoveInDate,
    amenities: persistedAmenities,
  });

  useEffect(() => {
    persistedSearchFiltersRef.current = {
      minMoveInDate: persistedMinMoveInDate,
      maxMoveInDate: persistedMaxMoveInDate,
      amenities: persistedAmenities,
    };
  }, [persistedMinMoveInDate, persistedMaxMoveInDate, persistedAmenities]);

  const load = useCallback((force = false) => {
    setLoading(true);
    setLoadError(null);
    void (async () => {
      try {
        // Shared loader — concurrent hook instances (and the
        // auto-enable provider) coalesce onto one request instead of
        // each issuing their own. `force` bypasses the cache but still
        // joins an in-flight request, so two instances forcing at the
        // same time still produce a single network call.
        if (!(await ensureSession())) return;
        const d = await loadListingAlert(force);

        const shouldBootstrapFromSearchFilters = !didAttemptBootstrapRef.current;

        didAttemptBootstrapRef.current = true;

        if (shouldBootstrapFromSearchFilters) {
          const bootstrapPatch: UpdateListingAlertRequest = {};
          const {
            minMoveInDate: bootstrapMinMoveInDate,
            maxMoveInDate: bootstrapMaxMoveInDate,
            amenities: bootstrapAmenities,
          } = persistedSearchFiltersRef.current;

          // NOTE: the three listing-alert basics (neighborhoods, max
          // rent, bedrooms) used to be bootstrapped here too. That
          // moved to `ListingAlertAutoEnable`, which syncs them
          // continuously rather than once per hook mount, and does so
          // on every route instead of only the pages that happen to
          // use this hook. Keeping a second writer here would let the
          // two disagree — this one fills blanks, that one mirrors the
          // latest filter values.
          //
          // Only the renter-profile extras are bootstrapped now; the
          // provider doesn't touch those.

          // Step 2 — move-in range. Bootstrap only if BOTH profile
          // bounds are unset AND the filters have at least one bound
          // to donate. We copy whatever bounds the filters provide
          // (even a single-sided range) — the wizard's chip UI can
          // project either shape back into a selection.
          const profileHasMoveIn =
            d.preferredMinMoveInDate !== null || d.preferredMaxMoveInDate !== null;
          const filterHasMoveIn =
            bootstrapMinMoveInDate !== null || bootstrapMaxMoveInDate !== null;
          if (!profileHasMoveIn && filterHasMoveIn) {
            bootstrapPatch.preferredMinMoveInDate = bootstrapMinMoveInDate;
            bootstrapPatch.preferredMaxMoveInDate = bootstrapMaxMoveInDate;
          }

          // Step 3 — amenities. Reverse-map the listings-filter
          // camelCase toggle keys to the renter-profile snake_case
          // codes (only the 8 amenities the profile tracks — the
          // filter has extras like `isUnitPetsAllowed` /
          // `isUnitPrivateOutdoor` / `isBuildingPool` /
          // `isBuildingOutdoor` that don't map to the profile UI).
          const AMENITY_FILTER_TO_PROFILE: Record<string, string> = {
            isUnitAirConditioned: 'is_unit_air_conditioned',
            isUnitDishwasher: 'is_unit_dishwasher',
            isUnitWasherDryer: 'is_unit_washer_dryer',
            isBuildingDoorman: 'is_building_doorman',
            isBuildingElevator: 'is_building_elevator',
            isBuildingGym: 'is_building_gym',
            isBuildingLaundryRoom: 'is_building_laundry_room',
            isBuildingParking: 'is_building_parking',
          };
          const filterAmenityCodes: string[] = [];
          if (bootstrapAmenities) {
            for (const [k, v] of Object.entries(bootstrapAmenities)) {
              if (v === true && AMENITY_FILTER_TO_PROFILE[k]) {
                filterAmenityCodes.push(AMENITY_FILTER_TO_PROFILE[k]);
              }
            }
          }
          if (isMissingArrayField(d.preferredAmenities) && filterAmenityCodes.length > 0) {
            bootstrapPatch.preferredAmenities = filterAmenityCodes;
          }

          // Pets — imply "yes" when the listings filter has the
          // `isUnitPetsAllowed` toggle on. There's no filter-side
          // equivalent of a "no pets" pick, so we only bootstrap the
          // affirmative case. Skipped whenever the profile already
          // has any value (yes OR no) so we never override an
          // explicit "no".
          if (d.preferredPets === null && bootstrapAmenities?.isUnitPetsAllowed === true) {
            bootstrapPatch.preferredPets = 'yes';
          }

          if (Object.keys(bootstrapPatch).length > 0) {
            const bootstrapped = await updateListingAlert(bootstrapPatch);
            // Publish so sibling instances see the bootstrapped row
            // rather than the pre-write one they just cached.
            setListingAlertCache(bootstrapped);
            setData(bootstrapped);
            return;
          }
        }

        setData(d);
      } catch (e) {
        setLoadError(e);
        setData(EMPTY_PROFILE);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Tracks the previous auth state so we can tell a login transition
  // apart from an ordinary mount.
  const wasLoggedInRef = useRef(isLoggedIn);

  useEffect(() => {
    const justLoggedIn = !wasLoggedInRef.current && isLoggedIn;
    wasLoggedInRef.current = isLoggedIn;
    if (!isLoggedIn) return;
    // Force past the cache on a login transition. The shared cache is
    // module-level, so it outlives both React remounts and dev HMR
    // updates — without this a login could silently reuse a row from
    // earlier in the page session (potentially a different user's,
    // if logout didn't run). Ordinary mounts still read the cache.
    load(justLoggedIn);
  }, [load, isLoggedIn]);

  // Adopt writes made by other consumers of the same row. Without
  // this, two mounted instances (e.g. the home pill and the wizard
  // modal it renders) would drift apart the moment either one saved —
  // the reason call sites previously needed manual `refetch()`.
  useEffect(() => {
    return subscribeToListingAlert(() => {
      const shared = getListingAlertCache();
      if (shared) setData(shared);
    });
  }, []);

  // Clear stale data on logout so the previously-loaded state doesn't
  // leak into a subsequent logged-out or different-user session. Also
  // reset the bootstrap latch so the next login re-runs the search-
  // filter bootstrap.
  useEffect(() => {
    if (!isLoggedIn) {
      setData(null);
      setLoading(false);
      setLoadError(null);
      didAttemptBootstrapRef.current = false;
      // Drop the shared row too — otherwise the next user on this
      // browser would read the previous user's cached data.
      clearListingAlertCache();
    }
  }, [isLoggedIn]);

  const updateListingAlertFields = useCallback(
    async (patch: UpdateListingAlertRequest) => {
      let previous: ListingAlertResponse | null = null;
      // Snapshot the pre-patch row synchronously so the auto-enable
      // decision below reads committed state rather than whatever the
      // optimistic setState has already applied.
      const before = dataRef.current;

      // ---- Auto-enable rule (client-owned; the BE no longer infers
      // this — see app/views/explorer.py) ----
      //
      // Turn alerts ON the moment the third basic lands. Three guards:
      //
      //  1. Caller didn't pass `listingAlert` itself. An explicit value
      //     always wins — that's the manual toggle on /saved and the
      //     wizard's deliberate opt-in, neither of which we override.
      //  2. This patch actually TOUCHES one of the three basics. Without
      //     it, editing amenities or commute while basics happen to be
      //     complete would re-enable alerts the user had deliberately
      //     switched off.
      //  3. Alerts are currently off and the post-patch state has all
      //     three set — i.e. a real false → true transition.
      //
      // Strictly one-directional: we never send `false` here. Clearing
      // a search filter must not disable alerts; only the /saved card
      // and the profile can turn them off, and those paths send the
      // flag explicitly.
      const callerSetFlag = 'listingAlert' in patch;
      const touchesBasics =
        'listingAlertNeighborhoodIds' in patch ||
        'listingAlertMaxRent' in patch ||
        'listingAlertNumBedrooms' in patch;
      // The one-shot marker gates ONLY the passive search-filter
      // automation (`ListingAlertAutoEnable`). It deliberately does
      // NOT gate this hook: /saved and the renter profile are surfaces
      // where the user is actively managing preferences, so completing
      // the basics there should always enable alerts. We still stamp
      // the marker on the way through — once alerts have been set up
      // here, background search activity has no business touching
      // them again.
      const autoEnableSpent = Boolean(before?.listingAlertAutoEnabledAt);
      // Preserve the original timestamp if one already exists.
      const stampIfUnset = autoEnableSpent
        ? {}
        : { listingAlertAutoEnabledAt: new Date().toISOString() };

      let outgoing = patch;
      let shouldFireAutoEnable = false;

      if (!callerSetFlag && touchesBasics && before && !before.listingAlert) {
        const merged = { ...before, ...patch };
        if (
          hasAllListingAlertBasics({
            neighborhoodIds: merged.listingAlertNeighborhoodIds,
            maxRent: merged.listingAlertMaxRent,
            bedrooms: merged.listingAlertNumBedrooms,
          })
        ) {
          outgoing = { ...patch, listingAlert: true, ...stampIfUnset };
          shouldFireAutoEnable = true;
        }
      } else if (callerSetFlag && patch.listingAlert === true) {
        // Explicit opt-in (wizard Basics-Next, /saved toggle). Stamp so
        // search automation stands down, but no auto event — the call
        // site fires its own.
        outgoing = { ...patch, ...stampIfUnset };
      }

      setData(d => {
        previous = d;
        const nextBase = d ?? EMPTY_PROFILE;
        return {
          ...nextBase,
          ...outgoing,
        };
      });
      setSaving(true);
      try {
        const next = await updateListingAlert(outgoing);
        setData(next);
        // Publish so every other consumer (sibling hooks + the
        // auto-enable provider) sees this write immediately.
        setListingAlertCache(next);
        // Only report a transition the server actually persisted.
        if (shouldFireAutoEnable && next.listingAlert) {
          analytics.track('instant_alerts_on_auto', {
            source: resolveListingAlertAutoSource(pathname),
          });
        }
        return next;
      } catch {
        if (previous) {
          setData(previous);
        } else {
          setData(EMPTY_PROFILE);
        }
        return undefined;
      } finally {
        setSaving(false);
      }
    },
    [analytics, pathname],
  );

  const setListingAlert = useCallback(
    async (on: boolean) => {
      await updateListingAlertFields({ listingAlert: on });
    },
    [updateListingAlertFields],
  );

  const setListingAlertNeighborhoodIds = useCallback(
    async (ids: string[] | null) => {
      const nextIds = ids ?? [];
      await updateListingAlertFields({ listingAlertNeighborhoodIds: nextIds });
    },
    [updateListingAlertFields],
  );

  const setListingAlertMaxRent = useCallback(
    async (maxRent: number | null) => {
      await updateListingAlertFields({ listingAlertMaxRent: maxRent });
    },
    [updateListingAlertFields],
  );

  const setListingAlertNumBedrooms = useCallback(
    async (bedrooms: number[] | null) => {
      const nextBedrooms =
        bedrooms && bedrooms.length > 0 ? [...bedrooms].sort((a, b) => a - b) : null;
      await updateListingAlertFields({ listingAlertNumBedrooms: nextBedrooms });
    },
    [updateListingAlertFields],
  );

  // ---------- Renter-profile setters (V1 extended preferences) ----------

  const setPreferredMoveInRange = useCallback(
    async (minDate: string | null, maxDate: string | null) => {
      // Move-in preference is a paired range on the BE — set both
      // ends together so we never leave a half-filled state.
      // Pass ISO date strings ("YYYY-MM-DD") to match the backend
      // DateField serialization.
      await updateListingAlertFields({
        preferredMinMoveInDate: minDate,
        preferredMaxMoveInDate: maxDate,
      });
    },
    [updateListingAlertFields],
  );

  const setPreferredPets = useCallback(
    async (value: PreferredPets | null) => {
      await updateListingAlertFields({ preferredPets: value });
    },
    [updateListingAlertFields],
  );

  const setPreferredPetsDetail = useCallback(
    async (value: string) => {
      await updateListingAlertFields({ preferredPetsDetail: value });
    },
    [updateListingAlertFields],
  );

  const setPreferredAmenities = useCallback(
    async (values: string[] | null) => {
      await updateListingAlertFields({
        preferredAmenities: values && values.length > 0 ? values : null,
      });
    },
    [updateListingAlertFields],
  );

  const setPreferredWorkLocation = useCallback(
    async (value: string) => {
      await updateListingAlertFields({ preferredWorkLocation: value });
    },
    [updateListingAlertFields],
  );

  const setPreferredSchoolLocation = useCallback(
    async (value: string) => {
      await updateListingAlertFields({ preferredSchoolLocation: value });
    },
    [updateListingAlertFields],
  );

  const setPreferredCommuteTime = useCallback(
    async (value: PreferredCommuteTime | null) => {
      await updateListingAlertFields({ preferredCommuteTime: value });
    },
    [updateListingAlertFields],
  );

  /**
   * Mark the wizard as complete. Called from the success screen's
   * "View matching listings" button — flips the flag so the home pill
   * / post-review CTA stop nagging. Server persists via the same
   * partial-patch endpoint.
   */
  const markProfileCompleted = useCallback(async () => {
    await updateListingAlertFields({ isRenterProfileCompleted: true });
  }, [updateListingAlertFields]);

  return {
    data,
    loading,
    saving,
    loadError,
    // Listing-alert basics setters (existing surface).
    setListingAlert,
    setListingAlertNeighborhoodIds,
    setListingAlertMaxRent,
    setListingAlertNumBedrooms,
    // Renter-profile setters (new).
    setPreferredMoveInRange,
    setPreferredPets,
    setPreferredPetsDetail,
    setPreferredAmenities,
    setPreferredWorkLocation,
    setPreferredSchoolLocation,
    setPreferredCommuteTime,
    markProfileCompleted,
    // Escape hatch — set multiple fields at once (used by the wizard
    // to save a whole step in one call).
    updateListingAlertFields,
    refetch: load,
  };
}

/**
 * Alias for the renter-profile UI code — same hook, self-documenting
 * name. The underlying endpoint and data shape are identical, so the
 * two names share state cleanly if both are used on the same page
 * (React de-dupes the request via the ref inside).
 */
export const useRenterProfile = useListingAlert;
